import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, MobileStepper as MuiMobileStepper, StepLabel as MuiStepLabel, Stepper as MuiStepper, Stack, Step, } from '@mui/material';
import { Icon, Typography } from '../../atoms';
import { useIsMobile } from '../../hooks';
import { useTheme } from '../../theme';
/**
 * The `StepProgress` component is used in customer-facing
 * applications to show users where they find themselves in the
 * calculator flow.
 *
 * @returns StepProgress component
 */
export function StepProgress({ steps, activeStepId }) {
    const theme = useTheme();
    const isMobile = useIsMobile();
    return (_jsx(_Fragment, { children: isMobile ? (_jsx(MobileStepper, { steps: steps, activeStepId: activeStepId, theme: theme })) : (_jsx(Stepper, { steps: steps, activeStepId: activeStepId, theme: theme })) }));
}
function Stepper({ steps, activeStepId, theme, }) {
    return (_jsx(MuiStepper, { activeStep: activeStepId, alternativeLabel: true, sx: {
            '.MuiStepConnector-root': {
                top: theme.spacing(106),
                left: `calc(-50% + ${theme.spacing(107)})`,
                right: `calc(50% + ${theme.spacing(107)})`,
            },
            '& .MuiStepConnector-root.Mui-completed': {
                borderColor: theme.palette.primary.main,
            },
            '& .MuiStepConnector-root.Mui-active': {
                borderColor: theme.palette.primary.dark,
            },
            '& .MuiStepConnector-root.Mui-disabled': {
                borderColor: theme.palette.grey[800],
            },
            '& .MuiStepConnector-line': {
                borderColor: 'inherit',
                borderTopWidth: '2px',
            },
        }, children: steps.map((label) => (_jsx(Step, { children: _jsx(StepLabel, { theme: theme, label: label }) }, label))) }));
}
function MobileStepper({ steps, activeStepId, theme, }) {
    return (_jsxs(Stack, { direction: "column", spacing: theme.spacing(105), justifyContent: "center", alignItems: "center", children: [_jsx(Typography, { variant: "h5", color: "darkBlue", children: steps[activeStepId ?? 0] }), _jsx(MuiMobileStepper, { steps: steps.length, activeStep: activeStepId, backButton: undefined, nextButton: undefined, position: "static", sx: {
                    background: 'transparent',
                    backgroudColor: 'transparent',
                    justifyContent: 'center',
                    '& .MuiMobileStepper-dot': {
                        height: theme.spacing(107),
                        width: theme.spacing(107),
                    },
                } })] }));
}
function StepLabel({ theme, label, }) {
    return (_jsx(MuiStepLabel, { StepIconComponent: StepIcon, sx: {
            '& .MuiStepLabel-label.Mui-completed': {
                color: theme.palette.primary.main,
            },
            '& .MuiStepLabel-label.Mui-disabled': {
                color: theme.palette.grey[800],
            },
            '& .MuiStepLabel-label.Mui-active': {
                color: theme.palette.primary.dark,
            },
        }, children: label }));
}
function StepIcon({ completed, active, icon }) {
    const theme = useTheme();
    const setIconColor = ({ active, completed, }) => {
        if (completed) {
            return { hex: theme.palette.primary.main, value: 'zurichBlue' };
        }
        if (active) {
            return { hex: theme.palette.primary.dark, value: 'darkBlue' };
        }
        return { hex: theme.palette.grey[800], value: 'grey8' };
    };
    const { hex, value } = setIconColor({ completed, active });
    return (_jsx(Box, { sx: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: theme.spacing(109),
            width: theme.spacing(109),
            borderRadius: '50%',
            border: completed ? 'unset' : `solid ${theme.spacing(101)} ${hex}`,
            color: completed ? theme.palette.grey.A100 : hex,
            backgroundColor: completed ? theme.palette.primary.main : 'unset',
        }, children: completed ? (_jsx(Icon, { variant: "check" })) : (_jsx(Typography, { variant: "body1 semibold", color: value, children: icon })) }));
}
